import type { ProductCardProps } from '@virginexperiencedays/components-v2/src/cards/Product';
import type { ProductGridCardProps } from '../search/ProductCard';

/**
 * Maps a DY recommendation item to a ProductCardProps object.
 *
 * @param item - The DY item to be mapped.
 * @returns The mapped ProductCardProps object or null if the item is invalid.
 */
export function mapDYItemToProductCard(item: unknown): ProductCardProps | null {
  if (typeof item !== 'object' || !item) return null;

  const {
    slug,
    sku,
    title,
    alt = '',
    src,
    averageRating,
    totalReviews,
    displayPrice,
    rrp,
    percentOff,
    isCollectionProduct = false,
    isNew = false,
    isMinimal = false,
    locations,
    productPromo,
  } = item as any;

  return {
    title,
    src,
    alt: alt ?? title,
    percentOff,
    displayPrice,
    rrp: isNaN(rrp) ? null : rrp,
    isNew,
    isOnSale: displayPrice !== rrp,
    sku,
    averageRating,
    totalReviews,
    isMinimal,
    isCollectionProduct,
    slug,
    noOfLocations: locations?.length || 0,
    productPromo,
  };
}

/**
 * Maps a DY recommendation item to a ProductCardV2Props object.
 *
 * @param item - The DY item to be mapped.
 * @param onClick - The function to be called when the product card is clicked.
 * @returns The mapped ProductCardProps object or null if the item is invalid.
 */
export function mapDYItemToProductCardV2(item: unknown): ProductGridCardProps['item'] | null {
  if (typeof item !== 'object' || !item) return null;

  const {
    sku,
    slug,
    title,
    alt = '',
    src,
    averageRating,
    displayPrice,
    rrp,
    percentOff,
    isCollectionProduct = false,
    locations,
    categories,
    productPromo,
    additionalImages,
    keywords,
  } = item as any;

  const rrpValue = isNaN(rrp) ? null : rrp;

  const images = [];

  const mainImage = src && isValidHttpsUrl(src) ? { src, alt: alt ?? title } : null;
  if (mainImage) images.push(mainImage);

  const additionalImagesArr = formatAdditionalImages(additionalImages);
  images.push(...additionalImagesArr);

  const categoriesArray = Array.isArray(categories) ? categories : [];
  const keywordsArray = Array.isArray(keywords) ? keywords : [];

  return {
    key: sku,
    title,
    slug,
    price: {
      rrp: rrpValue,
      displayPrice,
      percentOff,
    },
    reviews: {
      averageRating,
    },
    locations,
    categories: [...categoriesArray, ...keywordsArray],
    nearestLocation: null,
    productPromo,
    sku,
    isCollectionProduct,
    images,
  } as ProductGridCardProps['item'];
}

function formatAdditionalImages(images: string): { src: string; alt: string }[] {
  if (typeof images !== 'string') return [];
  const split = images.split('|');

  if (!Array.isArray(split) || !split.length) return [];

  return split
    .filter(isValidHttpsUrl)
    .map((url, i) => ({ src: url, alt: `additional-image-${i + 1}` }));
}

function isValidHttpsUrl(url: string): boolean {
  const httpsUrlRegex = /^https:\/\/[^\s/$.?#].[^\s]*$/;
  return httpsUrlRegex.test(url);
}
